import type { Ref } from "vue"
import type {
  ItemConsumeInfo,
  ItemOrderVO,
  ItemOrderVOExt,
  MemberTableDiscountInfo,
  MerchantOpeningTableVO,
  TableConsumeInfo,
  TableConsumeInfoExt,
  TableItemConsumeInfo,
} from "@/api/business/types/table"
import type { OrderDetailVO } from "@/api/user/types/member"
import { computed } from "vue"
import { resolveMemberBenefitNetAmount } from "@/utils/memberLevelBenefit"

export const getAfterDiscountOtherFee = (
  tableConsumeInfo: TableConsumeInfo,
  discountInfo: MemberTableDiscountInfo | undefined,
  isEnableDiscountTableFee: boolean,
) => {
  const { tableFee, serviceFee } = tableConsumeInfo
  if (!discountInfo || !isEnableDiscountTableFee) {
    return {
      tableFee: tableFee ?? 0,
      serviceFee: serviceFee ?? 0,
    }
  } else {
    return {
      tableFee: (tableFee ?? 0) * (discountInfo.tableDiscountRate ?? 1),
      serviceFee: (serviceFee ?? 0) * (discountInfo.serviceFeeDiscountRate ?? 1),
    }
  }
}

// 计算订单输入
export const calculateOrderIncome = (orderDetail: OrderDetailVO | undefined | null) => {
  if (!orderDetail) {
    return 0
  }
  const payAmount = orderDetail.orderInfo?.payAmount ?? 0
  const presentPayAmountTotal =
    orderDetail.ruledPaymentRecord?.paymentInfo?.reduce((acc, cur) => {
      const usesMemberBalanceRules = cur.paymentType === 2
        || (cur.paymentType === 7 && cur.paymentTypeId == null)
      if (usesMemberBalanceRules && cur.detail) {
        let present = 0
        if (cur.rechargeBalanceIncomeRule === 1) {
          present += cur.detail.realAmount ?? 0
        }
        if (cur.rewardBalanceIncomeRule === 1) {
          present += cur.detail.presentAmount ?? 0
        }
        return acc + present
      } else {
        if (cur.paymentIncomeRule === 1) {
          return acc + (cur.payAmount ?? 0)
        } else {
          return acc
        }
      }
    }, 0) ?? 0
  return payAmount - presentPayAmountTotal
}

export const calculateTableFeeList = (
  tableConsumeInfoList: TableConsumeInfo[] | undefined,
  tableDiscountInfo: MemberTableDiscountInfo | undefined,
  isEnableDiscountTableFee: boolean,
): TableConsumeInfoExt[] => {
  return (
    tableConsumeInfoList?.map((item) => {
      const { tableFee, serviceFee } = getAfterDiscountOtherFee(item, tableDiscountInfo, isEnableDiscountTableFee)
      return {
        ...item,
        afterDiscountTableFee: tableFee,
        afterDiscountServiceFee: serviceFee,
      }
    }) ?? []
  )
}

type AfterSalePriceItem = {
  salePrice?: ItemOrderVOExt["salePrice"]
  memberLevelDiscountInfo?: ItemOrderVOExt["memberLevelDiscountInfo"]
}

export const getAfterSalePrice = (product: AfterSalePriceItem, enableMemberPriceValue: boolean) => {
  const memberLevelDiscountInfo = product.memberLevelDiscountInfo
  if (!memberLevelDiscountInfo) {
    return product.salePrice ?? 0
  }
  if (memberLevelDiscountInfo.type === "memberDiscount") {
    return (product.salePrice ?? 0) * (memberLevelDiscountInfo.memberDiscount?.discount ?? 1)
  } else if (memberLevelDiscountInfo.type === "memberPrice"
    && (memberLevelDiscountInfo.memberPrice != null || enableMemberPriceValue)) {
    // 单个 memberPrice 是后端在落单时已经选中的快照，不能被后来变化的等级开关否决。
    return memberLevelDiscountInfo.memberPrice?.memberPrice ?? product.salePrice ?? 0
  }
  return product.salePrice ?? 0
}

type OrderConsumeTotalInput = {
  mergeOrderAmount?: number | null
  orderedProductAmount?: number | null
  pendingProductAmount?: number | null
  serviceFee?: number | null
  tableFee?: number | null
}

export const calculateOrderConsumeTotal = ({
  mergeOrderAmount,
  orderedProductAmount,
  serviceFee,
  tableFee,
}: OrderConsumeTotalInput) => {
  return (tableFee ?? 0) + (serviceFee ?? 0) + (orderedProductAmount ?? 0) + (mergeOrderAmount ?? 0)
}

export const calculateItemOrderNetAmount = (item: ItemOrderVOExt) => {
  if (item.isSend === 1 || item.brandPointRedeemed === true) {
    return 0
  }
  if (item.discountInfo) {
    return (item.count ?? 0) * (item.salePrice ?? 0) - (item.discountInfo.discountAmount ?? 0)
  }
  const count = item.count ?? 0
  const grossAmount = count * (item.salePrice ?? 0)
  const frozenMemberBenefitAmount = resolveMemberBenefitNetAmount(
    grossAmount,
    count,
    item.memberLevelDiscountInfo,
  )
  if (frozenMemberBenefitAmount != null) {
    return frozenMemberBenefitAmount
  }
  return (item.count ?? 0) * Math.round(item.afterDiscountPrice ?? 0)
}

export function useOrderMath(
  tableVo: Ref<MerchantOpeningTableVO | undefined>,
  tableDiscountInfo: Ref<MemberTableDiscountInfo | undefined>,
  snapshot?: TableItemConsumeInfo | undefined,
) {
  const enableMemberPrice = computed(() => {
    if (tableDiscountInfo.value?.enableMemberPrice === 1) {
      return true
    } else {
      return false
    }
  })

  const calculateSnapshotItemOrderList = (snapshot: TableItemConsumeInfo, enableMemberPriceValue: boolean) => {
    return snapshot.orders.flatMap((item) => {
      return item.map(
        (consumeItem: ItemOrderVO) =>
          ({
            ...consumeItem,
            afterDiscountPrice: getAfterSalePrice(consumeItem, enableMemberPriceValue),
          }) as ItemOrderVOExt,
      )
    })
  }

  const calculateItemOrderList = (
    itemConsumeInfoList: ItemConsumeInfo[] | undefined,
    enableMemberPriceValue: boolean,
    hasMemberLevelIdentity: boolean,
  ): ItemOrderVOExt[] => {
    const list =
      itemConsumeInfoList?.flatMap((item) => {
        return (item.itemConsumeList ?? []).map((consumeItem) => ({
          ...consumeItem,
          itemOrderId: item.itemOrderId,
          createTime: item.createTime,
        }))
      }) ?? []
    return list.map((item) => {
      return {
        ...item,
        afterDiscountPrice: hasMemberLevelIdentity ? getAfterSalePrice(item, enableMemberPriceValue) : (item.salePrice ?? 0),
      }
    })
  }

  const itemOrderList = computed<ItemOrderVOExt[]>(() => {
    if (snapshot) {
      return calculateSnapshotItemOrderList(snapshot, enableMemberPrice.value)
    }
    const hasMemberLevelIdentity = tableDiscountInfo.value?.memberLevelId != null
      || tableDiscountInfo.value?.brandMemberLevelId != null
    return calculateItemOrderList(tableVo.value?.itemConsumeInfoList, enableMemberPrice.value, hasMemberLevelIdentity)
  })

  const tableFeeList = computed<TableConsumeInfoExt[]>(() => {
    // JZTAG 默认为打折，具体折扣从memberDiscount中取
    const enableTableDiscount = true
    return calculateTableFeeList(tableVo.value?.tableConsumeInfoList, tableDiscountInfo.value, enableTableDiscount)
  })

  const tableFeeDiscountTotal = computed(() => {
    return tableFeeList.value.reduce((total, item) => {
      const tableFeeDiscount = (item.tableFee ?? 0) - (item.afterDiscountTableFee ?? 0)
      const serviceFeeDiscount = (item.serviceFee ?? 0) - (item.afterDiscountServiceFee ?? 0)
      return total + tableFeeDiscount + serviceFeeDiscount
    }, 0)
  })

  const itemDiscountTotal = computed(() => {
    return itemOrderList.value.reduce((total, item) => {
      if (item.isSend === 1) {
        return total
      } else {
        const count = item.count ?? 0
        const grossAmount = (item.salePrice ?? 0) * count
        const frozenMemberBenefitAmount = resolveMemberBenefitNetAmount(
          grossAmount,
          count,
          item.memberLevelDiscountInfo,
        )
        if (frozenMemberBenefitAmount != null) {
          return total + grossAmount - frozenMemberBenefitAmount
        }
        const priceDiff = (item.salePrice ?? 0) - (item.afterDiscountPrice ?? 0)
        return total + priceDiff * count
      }
    }, 0)
  })

  const canceledItemOrderList = computed(() => {
    const tableRecordId = tableVo.value?.tableRecordId
    if (!tableRecordId) {
      return []
    } else {
      return tableVo.value?.totalCanceledItemRecord[tableRecordId] || []
    }
  })

  const skuTotalNumber = computed(() => {
    return (
      itemOrderList.value?.reduce((total, item) => {
        return total + (item.count ?? 0)
      }, 0) || 0
    )
  })

  const productTotalAmount = computed(() => {
    return (
      itemOrderList.value?.reduce((total, item) => {
        return total + calculateItemOrderNetAmount(item)
      }, 0) || 0
    )
  })

  const allTableFee = computed(() => {
    return tableFeeList.value?.reduce((total, item) => {
      return total + (item.afterDiscountTableFee ?? 0)
    }, 0)
  })

  const allServiceFee = computed(() => {
    return tableFeeList.value?.reduce((total, item) => {
      return total + (item.afterDiscountServiceFee ?? 0)
    }, 0)
  })

  return {
    itemOrderList,
    tableFeeList,
    tableFeeDiscountTotal,
    itemDiscountTotal,
    enableMemberPrice,
    canceledItemOrderList,
    skuTotalNumber,
    productTotalAmount,
    allTableFee,
    allServiceFee,
  }
}
